import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { BarChart3, Crown, Medal, Lock, Loader2 } from "lucide-react";
import apiClient from "@/lib/apiClient";
import { PreviewLayout } from "@/components/PreviewLayout";
import { SignInGateModal } from "@/components/SignInGateModal";
import { EmptyState, FullPageLoader } from "@/components/common";

const categories = [
  { key: "overall", label: "Overall" },
  { key: "points", label: "Points" },
  { key: "hours", label: "Hours" },
  { key: "tournaments", label: "Tournaments" },
];

const rankColor = (rank) => {
  if (rank === 1) return "#ffb020";
  if (rank === 2) return "#cbd5e1";
  if (rank === 3) return "#f59e0b";
  return "#64748b";
};

export default function PreviewLeaderboards() {
  const [flagsChecked, setFlagsChecked] = useState(false);
  const [allowed, setAllowed] = useState(false);
  const [category, setCategory] = useState("overall");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [gateOpen, setGateOpen] = useState(false);

  useEffect(() => {
    apiClient.get("/public/guest-flags")
      .then(({ data }) => setAllowed(!!data.browse_leaderboards))
      .catch(() => setAllowed(false))
      .finally(() => setFlagsChecked(true));
  }, []);

  useEffect(() => {
    if (!allowed) return;
    setLoading(true);
    apiClient.get("/leaderboards", { params: { category, limit: 50 } })
      .then(({ data }) => setRows(data))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [category, allowed]);

  const metricFor = (row) => {
    if (category === "hours") return `${row.total_hours} hrs`;
    if (category === "tournaments") return `${row.total_sessions} events`;
    return `${row.score?.toLocaleString()} pts`;
  };

  if (!flagsChecked) return <FullPageLoader label="Loading" />;
  if (!allowed) return <Navigate to="/login" replace />;

  return (
    <PreviewLayout tab="leaderboards">
      <div className="mb-6">
        <h1 className="font-display text-3xl sm:text-4xl font-black text-white uppercase tracking-wide">Leaderboards</h1>
        <p className="text-slate-400 mt-1.5">See the top players. Sign in to appear on the board.</p>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {categories.map((c) => (
          <button key={c.key} onClick={() => setCategory(c.key)}
            data-testid={`preview-leaderboard-tab-${c.key}`}
            className="px-5 py-2.5 rounded-xl font-display font-semibold uppercase tracking-wide text-sm transition-all"
            style={category === c.key
              ? { background: "linear-gradient(90deg,#00e5ff,#8b5cf6)", color: "#05060d" }
              : { background: "rgba(8,11,22,0.6)", border: "1px solid rgba(120,150,220,0.14)", color: "#94a3b8" }}>
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading rankings…</div>
      ) : rows.length === 0 ? (
        <EmptyState icon={BarChart3} title="No rankings yet" subtitle="Rankings will appear once players earn points." />
      ) : (
        <div className="glass rounded-2xl overflow-hidden">
          {rows.map((row) => (
            <button key={row.id}
              onClick={() => setGateOpen(true)}
              className="w-full text-left flex items-center gap-4 px-5 py-4 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors"
              data-testid={`preview-leaderboard-row-${row.rank}`}>
              <div className="w-10 flex justify-center">
                {row.rank <= 3 ? (
                  <Medal className="w-6 h-6" style={{ color: rankColor(row.rank) }} />
                ) : (
                  <span className="font-display font-bold text-slate-500">#{row.rank}</span>
                )}
              </div>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center font-display font-bold text-sm text-black shrink-0"
                style={{ background: "linear-gradient(135deg,#00e5ff,#8b5cf6)" }}>
                {row.username?.split(" ").map((n) => n[0]).join("").slice(0, 2).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-semibold truncate">{row.username}</p>
                <p className="text-slate-500 text-xs flex items-center gap-2">
                  <Crown className="w-3 h-3" /> Lvl {row.level} · {row.win_rate}% WR
                </p>
              </div>
              <div className="text-right">
                <p className="font-display font-bold neon-cyan-text">{metricFor(row)}</p>
              </div>
              <Lock className="w-4 h-4 text-slate-600 shrink-0" />
            </button>
          ))}
        </div>
      )}

      <SignInGateModal
        open={gateOpen}
        onClose={() => setGateOpen(false)}
        feature="view player profiles"
        message="Sign in or create an account to view detailed profiles, track your ranking and earn rewards."
      />
    </PreviewLayout>
  );
}
